import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';

// Layouts
import { MainLayout } from './layouts/MainLayout';
import { AuthLayout } from './layouts/AuthLayout';

// Auth pages
import { LoginPage } from './pages/auth/LoginPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { AcceptInvitePage } from './pages/auth/AcceptInvitePage';

// Main pages
import { DashboardPage } from './pages/DashboardPage';
import { JournalListPage } from './pages/journals/JournalListPage';
import { JournalDetailPage } from './pages/journals/JournalDetailPage';
import { JournalCreatePage } from './pages/journals/JournalCreatePage';
import { AutoJournalPage } from './pages/journals/AutoJournalPage';
import { LedgerPage } from './pages/journals/LedgerPage';
import { TrialBalancePage } from './pages/journals/TrialBalancePage';
import { EvidenceListPage } from './pages/evidence/EvidenceListPage';
import { EvidenceDetailPage } from './pages/evidence/EvidenceDetailPage';
import { FixedAssetListPage } from './pages/assets/FixedAssetListPage';
import { TaxAdjustmentPage } from './pages/tax/TaxAdjustmentPage';
import { TaxSchedulePage } from './pages/tax/TaxSchedulePage';
import { ConsumptionTaxPage } from './pages/tax/ConsumptionTaxPage';
import { AccountListPage } from './pages/master/AccountListPage';
import { DepartmentListPage } from './pages/master/DepartmentListPage';
import { PartnerListPage } from './pages/master/PartnerListPage';
import { UserListPage } from './pages/admin/UserListPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { FiscalPeriodsPage } from './pages/admin/FiscalPeriodsPage';
import { ProfilePage } from './pages/ProfilePage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { FinancialStatementsPage } from './pages/journals/FinancialStatementsPage';
import { BankAccountsPage } from './pages/bank/BankAccountsPage';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

// Admin Route wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      </Route>

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route path="/" element={<DashboardPage />} />
        
        {/* Journals / Accounting */}
        <Route path="/journals" element={<JournalListPage />} />
        <Route path="/journals/create" element={<JournalCreatePage />} />
        <Route path="/journals/:id" element={<JournalDetailPage />} />
        <Route path="/journals/auto" element={<AutoJournalPage />} />
        <Route path="/ledger" element={<LedgerPage />} />
        <Route path="/trial-balance" element={<TrialBalancePage />} />
        <Route path="/financial-statements" element={<FinancialStatementsPage />} />
        
        {/* Bank */}
        <Route path="/bank/accounts" element={<BankAccountsPage />} />
        
        {/* Evidence */}
        <Route path="/evidence" element={<EvidenceListPage />} />
        <Route path="/evidence/:id" element={<EvidenceDetailPage />} />
        
        {/* Fixed Assets */}
        <Route path="/assets" element={<FixedAssetListPage />} />
        
        {/* Tax */}
        <Route path="/tax/adjustments" element={<TaxAdjustmentPage />} />
        <Route path="/tax/schedules" element={<TaxSchedulePage />} />
        <Route path="/tax/consumption" element={<ConsumptionTaxPage />} />
        
        {/* Master Data */}
        <Route path="/master/accounts" element={<AccountListPage />} />
        <Route path="/master/departments" element={<DepartmentListPage />} />
        <Route path="/master/partners" element={<PartnerListPage />} />
        
        {/* Admin */}
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <UserListPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <AdminRoute>
              <SettingsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/fiscal-periods"
          element={
            <AdminRoute>
              <FiscalPeriodsPage />
            </AdminRoute>
          }
        />
        
        {/* Profile */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

